import React, { useState, useEffect, useRef } from 'react';
import { TerminalCommand } from '../data/chapters';

interface TerminalProps {
  commands?: TerminalCommand[];
  interactive?: boolean;
  title?: string;
  hideSectionTitle?: boolean;
  hideTerminalTitleBar?: boolean;
}

function StaticTerminal({ commands, title }: { commands: TerminalCommand[]; title: string }) {
  const [visibleLines, setVisibleLines] = useState<number>(0);

  useEffect(() => {
    setVisibleLines(0);
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setVisibleLines(i);
      if (i >= commands.length) clearInterval(interval);
    }, 400);
    return () => clearInterval(interval);
  }, [commands]);

  return (
    <div className="terminal-block">
      <div className="terminal-titlebar">
        <div className="terminal-dot red" />
        <div className="terminal-dot yellow" />
        <div className="terminal-dot green" />
        <span className="terminal-titlebar-text">{title}</span>
      </div>
      <div className="terminal-body">
        {commands.slice(0, visibleLines).map((cmd, i) => (
          <div key={i}>
            <div className="terminal-line">
              <span className="terminal-prompt">$ </span>
              <span className="terminal-cmd">{cmd.command}</span>
            </div>
            <div className="terminal-line terminal-output">{cmd.output}</div>
          </div>
        ))}
        {visibleLines < commands.length && (
          <div className="terminal-line">
            <span className="terminal-prompt">$ </span>
            <span className="terminal-cursor" />
          </div>
        )}
      </div>
    </div>
  );
}

const INTERACTIVE_COMMANDS: Record<string, string> = {
  help: `available commands:
  help          show this message
  about         about this OS project
  arch          show target architectures
  memory        explain virtual memory
  syscall       explain syscalls
  panic         simulate a kernel panic
  clear         clear terminal`,
  about: `OS Journal — 3 operating systems built from scratch
  Joe's OS   → x86, C++, VGA text output
  OSx86      → x86 bootloader + protected mode
  RISC-V OS  → virtual memory, processes, shell`,
  arch: `supported architectures:
  x86    → 32-bit, BIOS boot, VGA 0xB8000
  riscv  → 32-bit, OpenSBI, SV32 paging`,
  memory: `SV32 virtual address layout:
  [31:22] VPN[1]  → index into L1 page table (1024 entries)
  [21:12] VPN[0]  → index into L2 page table (1024 entries)
  [11:0]  offset  → byte offset within 4KB page

  satp register = SATP_SV32 | (root_page_table >> 12)`,
  syscall: `syscall convention (RISC-V):
  a3 = syscall number
  a0 = arg0, a1 = arg1, a2 = arg2
  a0 = return value

  SYS_PUTCHAR = 1  → write char to console
  SYS_GETCHAR = 2  → read char from console`,
  panic: `PANIC: kernel.c:42: unexpected trap scause=0000000f, stval=84241000, sepc=80200048

  scause 0xf = store page fault
  stval  = faulting address (past free_ram_end)
  sepc   = kernel_entry + 0x24

  cause: sscratch held wrong kernel stack address
  fix:   csrw sscratch, kernel_stack_top after trap entry`,
  clear: '__clear__',
};

function InteractiveTerminal({ hideTitleBar = false }: { hideTitleBar?: boolean }) {
  const [history, setHistory] = useState<{ type: 'cmd' | 'out'; text: string }[]>([
    { type: 'out', text: 'RISC-V OS kernel terminal — type "help" for commands' },
  ]);
  const [input, setInput] = useState('');
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (bodyRef.current)
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [history]);

  const run = (cmd: string) => {
    const trimmed = cmd.trim().toLowerCase();
    if (!trimmed) return;

    const output = INTERACTIVE_COMMANDS[trimmed];

    if (output === '__clear__') {
      setHistory([{ type: 'out', text: 'terminal cleared.' }]);
      return;
    }

    setHistory((h) => [
      ...h,
      { type: 'cmd', text: cmd },
      { type: 'out', text: output ?? `command not found: ${trimmed} — try "help"` },
    ]);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      run(input);
      setInput('');
    }
  };

  return (
    <div className="interactive-terminal" onClick={() => inputRef.current?.focus()}>
      {!hideTitleBar && (
        <div className="terminal-titlebar">
          <div className="terminal-dot red" />
          <div className="terminal-dot yellow" />
          <div className="terminal-dot green" />
          <span className="terminal-titlebar-text">kernel shell — interactive</span>
        </div>
      )}
      <div className="interactive-body" ref={bodyRef}>
        {history.map((line, i) => (
          <div key={i} className="terminal-line">
            {line.type === 'cmd' ? (
              <>
                <span className="terminal-prompt">$ </span>
                <span className="terminal-cmd">{line.text}</span>
              </>
            ) : (
              <span className="terminal-output">{line.text}</span>
            )}
          </div>
        ))}
        <div className="interactive-input-line">
          <span className="terminal-prompt">$ </span>
          <input
            ref={inputRef}
            className="interactive-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            spellCheck={false}
            autoComplete="off"
          />
        </div>
      </div>
    </div>
  );
}

export default function Terminal({ commands = [], interactive, title = 'bash', hideSectionTitle = false, hideTerminalTitleBar = false }: TerminalProps) {
  return (
    <div className="terminal-section">
      {!hideSectionTitle && <div className="terminal-section-title">{"// try it"}</div>}
      {interactive && <InteractiveTerminal hideTitleBar={hideTerminalTitleBar} />}
      {commands.length > 0 && <StaticTerminal commands={commands} title={title} />}
    </div>
  );
}
